import React, { useState, useEffect } from "react";
import {
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Switch,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Snackbar,
  Alert,
  CircularProgress
} from "@mui/material";
import { axiosInstance } from "../../services/axiosInstance";
import { endpoints } from "../../services/endpoint";

const getUtcDate = (d = new Date()) => {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const getUtcTime = (d = new Date()) => {
  const h = String(d.getUTCHours()).padStart(2, "0");
  const min = String(d.getUTCMinutes()).padStart(2, "0");
  return `${h}:${min}`;
};

const HolidayPage = ({ holiday }) => {
  const [toggleStates, setToggleStates] = useState({});
  const [open, setOpen] = useState(false);
  const [selectedQueue, setSelectedQueue] = useState(null);
  const [loading, setLoading] = useState(false);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [isBulkAction, setIsBulkAction] = useState(false);

  const customerAWSAccountId = localStorage.getItem("CustomerAWSAccountId");
  const region = localStorage.getItem("Region");

  const storedInstance = localStorage.getItem("SelectedInstance");
  let parsedInstance = {};
  if (storedInstance) {
    try { parsedInstance = JSON.parse(storedInstance); } catch (e) {}
  }
  const instanceAlias = parsedInstance.InstanceAlias || localStorage.getItem("instanceName");

  const todayUtc = getUtcDate(new Date());

  useEffect(() => {
    if (holiday && holiday.length > 0) {
      const initialStates = {};

      holiday.forEach((q) => {
        initialStates[q.arn] = !!q.holidayStatus;
      });

      setToggleStates(initialStates);
    }
  }, [holiday]);

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedQueue(null);
    setIsBulkAction(false);
    setStartDate("");
    setEndDate("");
    setStartTime("");
    setEndTime("");
    setLoading(false);
  };

  // ✅ Toggle
  const handleToggle = async (queue) => {
    const isEnabled = !!toggleStates[queue.arn];

    if (isEnabled) {
      await disableHoliday(queue);
    } else {
      const now = new Date();
      setSelectedQueue(queue);
      setStartDate(getUtcDate(now));
      setEndDate(getUtcDate(now)); // ✅ FIX
      setStartTime(getUtcTime(now));
      setEndTime("23:59");
      setOpen(true);
    }
  };

  // ✅ Global Enable
  const handleEnableAll = () => {
    if (!holiday.length) {
      showSnackbar("No queues available", "error");
      return;
    }

    const now = new Date();
    setIsBulkAction(true);
    setSelectedQueue(null);
    setStartDate(getUtcDate(now));
    setEndDate(getUtcDate(now)); // ✅ FIX
    setStartTime(getUtcTime(now));
    setEndTime("23:59");
    setOpen(true);
  };

  const handleDisableAll = async () => {
    if (!holiday.length) {
      showSnackbar("No queues available", "error");
      return;
    }

    setLoading(true);

    const body = {
      Queues: holiday.map(q => q.name),
      HolidayStatus: false, 
      StartDate: getUtcDate(),
      EndDate: getUtcDate(),
      StartTime: getUtcTime(),
      EndTime: "23:59",
      InstanceAlias: instanceAlias,
      region,
      awsAccount: customerAWSAccountId,
    };

    try {
      const res = await axiosInstance.post(
        endpoints.globalEnable.holidayEnableDisable,
        body
      );

      if (res.status === 200 || res.data?.statusCode === 200) {
        const updated = {};
        holiday.forEach(q => (updated[q.arn] = false));
        setToggleStates(updated);

        showSnackbar("Holiday disabled for all queues!", "success");
      } else {
        showSnackbar("Failed to disable all", "error");
      }
    } catch (err) {
      showSnackbar("API error while disabling", "error");
    }

    setLoading(false);
  };

  // ✅ Submit
  const handleSubmitHoliday = async () => {
    if (!startDate || !endDate || !startTime || !endTime) {
      showSnackbar("Please fill all fields", "error");
      return;
    }

    const start = new Date(`${startDate}T${startTime}:00Z`);
    const end = new Date(`${endDate}T${endTime}:00Z`);

    if (end <= start) {
      showSnackbar("End date/time must be greater than start date/time", "error");
      return;
    }

    setLoading(true);

    let body;

    if (isBulkAction) {
      body = {
        Queues: holiday.map(q => q.name),
        HolidayStatus: true,
        StartDate: startDate,
        EndDate: endDate,
        StartTime: startTime,
        EndTime: endTime,
        InstanceAlias: instanceAlias,
        region,
        awsAccount: customerAWSAccountId
      };
    } else {
      body = {
        Queue: selectedQueue.name,
        HolidayStatus: true,
        StartDate: startDate,
        EndDate: endDate,
        StartTime: startTime,
        EndTime: endTime,
        InstanceAlias: instanceAlias,
        region,
        awsAccount: customerAWSAccountId
      };
    }

    try {
      const res = await axiosInstance.post(
        endpoints.globalEnable.holidayEnableDisable,
        body
      );

      if (res.status === 200 || res.data?.statusCode === 200) {
        if (isBulkAction) {
          const updated = {};
          holiday.forEach(q => (updated[q.arn] = true));
          setToggleStates(updated);
          showSnackbar("Holiday enabled for all queues!", "success");
        } else {
          setToggleStates(prev => ({
            ...prev,
            [selectedQueue.arn]: true
          }));
          showSnackbar("Holiday enabled successfully!", "success");
        }

        handleClose();
      } else {
        showSnackbar("Failed to submit", "error");
      }
    } catch {
      showSnackbar("API error", "error");
    }

    setLoading(false);
  };

  // ✅ Disable
  const disableHoliday = async (queue) => {
    const body = {
      Queue: queue.name,
      HolidayStatus: false, // ✅ FIXED
      StartDate: getUtcDate(),
      EndDate: getUtcDate(),
      StartTime: getUtcTime(),
      EndTime: "23:59",
      InstanceAlias: instanceAlias,
      region,
      awsAccount: customerAWSAccountId
    };

    try {
      const res = await axiosInstance.post(
        endpoints.globalEnable.holidayEnableDisable,
        body
      );

      if (res.status === 200 || res.data?.statusCode === 200) {
        setToggleStates(prev => ({
          ...prev,
          [queue.arn]: false
        }));
        showSnackbar(`Holiday disabled for ${queue.name}`, "success");
      }
    } catch {
      showSnackbar("Disable failed", "error");
    }
  };

  return (
    <Paper sx={{ p: 2 }}>
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "16px"
      }}>
        <Typography variant="h5" style={{ textAlign: "center", flex: 1 }}>
          Holidays - Queue List
        </Typography>

        <div style={{ display: "flex", gap: "8px" }}>
          <Button
            variant="contained"
            onClick={handleEnableAll}
            size="small"
            sx={{
              backgroundColor: "#ef4444",
              fontSize: "11px",
              padding: "3px 8px",
              minWidth: "110px",
              "&:hover": {
                backgroundColor: "#dc2626"
              }
            }}
          >
            Enable Holidays
          </Button>
          <Button
            variant="contained"
            onClick={handleDisableAll}
            size="small"
            sx={{
              backgroundColor: "#6b7280",
              fontSize: "11px",
              padding: "3px 8px",
              minWidth: "110px"
            }}
          >
            Disable Holidays
          </Button>
        </div>
      </div>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell><strong>Queue Name</strong></TableCell>
            <TableCell><strong>Description</strong></TableCell>
            <TableCell><strong>Holiday</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {holiday.map((queue, index) => (
            <TableRow key={index}>
              <TableCell>{queue.name}</TableCell>
              <TableCell>{queue.description}</TableCell>
              <TableCell>
                <Switch
                  checked={!!toggleStates[queue.arn]}
                  onChange={() => handleToggle(queue)}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          Holiday Schedule
          {isBulkAction
            ? " - All Queues"
            : selectedQueue && ` - ${selectedQueue.name}`}
        </DialogTitle>

        <DialogContent>
          <div className="addusers-user-form-fields">
            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: todayUtc }}
              fullWidth
              margin="normal"
            />

            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: startDate || todayUtc }}
              fullWidth
              margin="normal"
            />

            <TextField
              label="Start Time (UTC)"
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              margin="normal"
            />

            <TextField
              label="End Time (UTC)"
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              margin="normal"
            />
          </div>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleSubmitHoliday}
            disabled={loading}
            startIcon={
              loading ? <CircularProgress size={20} color="inherit" /> : null
            }
          >
            {loading ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default HolidayPage;